import React, { Suspense, lazy, useEffect } from 'react'
import { Route, Switch, useLocation } from 'react-router-dom'
import Loader from 'components/Loader/Loader'

import { selectAvailableZones, selectCurrentUser, setCurrentZone } from 'store/global'
import { setDisplayedColumns } from 'store/businesses'
import { setHostsDisplayedColumns } from 'store/rentals'

import localStorageService from 'services/storage'

import { useDispatch, useSelector } from 'react-redux'
import { QueryParamProvider } from 'use-query-params'
import { useMatomo } from '@datapunt/matomo-tracker-react'

import styles from './App.scss'
import AuthWrapper from 'components/AuthWrapper'
import LoginPage from 'containers/Login/Login'

const Portal = lazy(() => import(/* webpackChunkName: "portal" */ /* webpackMode: "lazy" */ './Portal'))

function App() {
  const dispatch = useDispatch()
  const query = new URLSearchParams(useLocation().search)
  const { pushInstruction } = useMatomo()
  const currentUser: Record<string, string> = useSelector(selectCurrentUser)
  const availableZones = useSelector(selectAvailableZones)
  const location = useLocation()
  const { pathname = '', search } = location || {}

  const restoreLastBusinessesDisplayedColumns = () => {
    const columns = localStorageService.displayedColumns
    const hostColumns = localStorageService.displayedSTRColumns

    if (columns) dispatch(setDisplayedColumns({ columns }))
    if (hostColumns) dispatch(setHostsDisplayedColumns({ columns: hostColumns }))
  }

  const onAppInitialLoad = () => {
    localStorageService.returnUrl = `${pathname}${search}`

    restoreLastBusinessesDisplayedColumns()
  }
  // TODO: make this stand in react best practice
  useEffect(onAppInitialLoad, [])

  const restoreLastSelectedZone = () => {
    if (!currentUser || !currentUser.email || !availableZones.length) return

    const userSelectedZoneId = query.get('userSelectedZoneId')

    const { email, zoneId } = localStorageService.lastSelectedZoneForUser

    if (email !== currentUser.email) {
      if (availableZones?.length) dispatch(setCurrentZone({ zone: availableZones[0] }))
      return
    }

    const lastZoneId = parseInt(userSelectedZoneId || zoneId || '0')
    const nextZone = availableZones.find(({ id }: { id: number }) => id === lastZoneId)

    dispatch(setCurrentZone({ zone: nextZone }))
  }

  useEffect(() => {
    restoreLastSelectedZone()
    const name = currentUser ? currentUser.email : undefined
    pushInstruction('setUserId', name)
    // TODO fix dependency array and the used function
  }, [currentUser, availableZones.length])

  const isEmptyOrEmptyObject = (value: object) => {
    if (!value) return true
    if (Array.isArray(value)) return value.length === 0
    if (typeof value === 'object') {
      return Object.keys(value).length === 0
    }
    return true
  }

  return (
    <QueryParamProvider ReactRouterRoute={Route}>
      <div className={styles.appContainer}>
        <AuthWrapper>
          {isEmptyOrEmptyObject(currentUser) ? (
            <Loader type='globalLoading' message={''} />
          ) : (
            <Suspense fallback={<Loader type='globalLoading' message={''} />}>
              <Portal />
            </Suspense>
          )}
        </AuthWrapper>
        <Switch>
          <Route path='/login'>
            <LoginPage />
          </Route>
        </Switch>
      </div>
    </QueryParamProvider>
  )
}

export default App
